import React from "react";
import { Link } from "react-router-dom";

import { faInfoCircle, faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faArrowDown } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { Node as ProseNode } from "prosemirror-model";

import { ReactComponent as FellowWordmark } from "~/legacy-assets/images/logos/fellow-wordmark-black-solid.svg";
import { closeMeetSidePanel } from "~/lib/browserExtension";
import { EditorConfig, ProseReact } from "~/lib/prose";
import { getAllMarks } from "~/lib/prose/marks";
import { BulletPoint, HardBreak, Header, NumberedListItem, TextNode, Unstyled } from "~/lib/prose/nodes";
import { Agenda, Indent, Todo } from "~/lib/prose/plugins";
import BlockBoundary from "~/lib/prose/plugins/blockBoundary";
import { Button, Tooltip } from "~/ui";

import bg from "../images/Extension-BG@2x.png";

import styles from "../css/GoogleMeetPreAuth.module.css";

const FAKE_NOTE_EDITOR_CONFIG = new EditorConfig({
	extensions: [
		new BlockBoundary(),
		...getAllMarks(),
		new TextNode(),
		new Indent(),
		new Unstyled(),
		new BulletPoint(),
		new NumberedListItem(),
		new HardBreak(),
		new Header(),
		new Todo({ attendees: [], disabledReadOnly: true }),
		new Agenda({ attendees: [], disabledReadOnly: true }),
	],
	nodes: {
		doc: {
			content: "block+",
		},
	},
});

const FAKE_NOTE_CONTENT = {
	type: "doc",
	content: [
		{
			type: "header-one",
			attrs: {
				blockId: "0",
				level: "1",
			},
			content: [
				{
					type: "text",
					text: "Talking Points",
				},
				{
					type: "hard_break",
				},
				{
					type: "text",
					text: "The things to talk about",
				},
			],
		},
		{
			type: "agenda",
			attrs: {
				blockId: "1",
				indent: 0,
				done: false,
				wontDo: false,
				assignees: [],
				creator: null,
				placeholder: "",
				statusCondition: "SINGLE",
			},
		},
		{
			type: "header-one",
			attrs: {
				blockId: "2",
				creator: "VXNlcjoyNw==",
				level: "1",
			},
			content: [
				{
					type: "text",
					text: "Action Items",
				},
				{
					type: "hard_break",
				},
				{
					type: "text",
					text: "What came out of this meeting? What are your next steps?",
				},
			],
		},
		{
			type: "todo",
			attrs: {
				blockId: "3",
				indent: 0,
				done: false,
				wontDo: false,
				assignees: [],
				creator: null,
				placeholder: "",
				statusCondition: "SINGLE",
			},
		},
		{
			type: "header-one",
			attrs: {
				blockId: "4",
				level: "1",
				placeholder: "",
			},
			content: [
				{
					type: "text",
					text: "Notepad",
				},
				{
					type: "hard_break",
				},
				{
					type: "text",
					text: "Anything else to write down?",
				},
			],
		},
		{
			type: "bullet-point",
			attrs: {
				blockId: "5",
				done: false,
				assignees: [],
				creator: null,
				indent: 1,
			},
		},
	],
};

const FAKE_NOTE_CONTENT_NODE = ProseNode.fromJSON(FAKE_NOTE_EDITOR_CONFIG.schema, FAKE_NOTE_CONTENT);

type Props = {
	defaultMeetingCostPerHour?: number;
};

function makeURLWithCurrentURLParams(newPath: string, newParams: URLSearchParams) {
	const isAbsolute = newPath.startsWith("http");
	const urlParams = new URLSearchParams(window.location.search);
	urlParams.forEach((value, key) => {
		newParams.set(key, value);
	});
	const newURL = new URL(window.location.origin + newPath);
	newURL.search = newParams.toString();
	if (isAbsolute) {
		return newURL.toString();
	}
	return newURL.pathname + newURL.search;
}

function parseDurationHoursFromGoogleMeetInfo(durationStr: string) {
	let result: number | null = null;
	try {
		result = parseDurationHoursSingleDayFromGoogleMeetInfo(durationStr);
	} catch (err) {}
	if (!result) {
		try {
			result = parseDurationHoursMultiDayFromGoogleMeetInfo(durationStr);
		} catch (err) {}
	}
	return result;
}

function parseDurationHoursSingleDayFromGoogleMeetInfo(durationStr: string) {
	// Example input: Thu, Sep 7, 2023 2:45 PM - 3:45 PM
	const [_, startStr, endStr] = durationStr.match(/(\d+:\d+\s+[AP]M) - (\d+:\d+\s+[AP]M)/) || [];
	if (!startStr || !endStr) {
		return null;
	}
	const [startHourStr, startMinuteStr] = startStr.split(":");
	const [endHourStr, endMinuteStr] = endStr.split(":");
	const startHour = parseInt(startHourStr, 10);
	const startMinute = parseInt(startMinuteStr, 10);
	const endHour = parseInt(endHourStr, 10);
	const endMinute = parseInt(endMinuteStr, 10);
	const start = startHour * 60 + startMinute;
	const end = endHour * 60 + endMinute;
	return (end - start) / 60;
}

function parseDurationHoursMultiDayFromGoogleMeetInfo(durationStr: string) {
	const [startStr, endStr] = durationStr.split(" - ");
	if (!startStr || !endStr) return 0;
	const start = new Date(startStr);
	const end = new Date(endStr);
	return (end.getTime() - start.getTime()) / 1000 / 60 / 60;
}

function formatCostNumber(cost: number) {
	const locale = Intl.NumberFormat().resolvedOptions().locale;

	return new Intl.NumberFormat(locale, { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(cost);
}

export function GoogleMeetPreAuth({ defaultMeetingCostPerHour }: Props) {
	const urlParams = new URLSearchParams();
	urlParams.set("skipPreAuth", "1");
	const loginURL = makeURLWithCurrentURLParams("/auth/login/", urlParams);
	const signupURL = makeURLWithCurrentURLParams("/auth/signup/", urlParams);
	const [meetingCost, setMeetingCost] = React.useState<number | undefined>(defaultMeetingCostPerHour);
	const [meetingDuration, setMeetingDuration] = React.useState<number | undefined>();
	const gotResponseRef = React.useRef(false);
	const requestInFlightRef = React.useRef(false);
	const onCloseMeetingPanel = React.useCallback(() => {
		closeMeetSidePanel({ noAuth: true });
	}, []);
	React.useEffect(() => {
		function handleWindowMessage(event: MessageEvent) {
			if (
				event.data?.type === "BROWSER_EXTENSION_NOTES_TOGGLED" &&
				event.data.notesVisible &&
				!gotResponseRef.current &&
				!requestInFlightRef.current
			) {
				window.parent.postMessage({ type: "REQ_MEETING_COST_NO_AUTH", notesManuallyOpened: true }, "*");
				requestInFlightRef.current = true;
			}
			if (event.data?.type === "MEETING_COST_NO_AUTH_RESULT") {
				gotResponseRef.current = true;
				requestInFlightRef.current = false;
				const salary = parseInt(event.data.salary, 10) || 100_000;
				const hourlyRate = salary / 52 / 40;
				if (event.data.attendeeCount && hourlyRate) {
					setMeetingCost(event.data.attendeeCount * hourlyRate);
				}
				try {
					const durationHours = parseDurationHoursFromGoogleMeetInfo(event.data.duration);
					if (durationHours) {
						setMeetingDuration(durationHours);
					}
				} catch (err) {}
			}
		}
		window.addEventListener("message", handleWindowMessage);
		window.parent.postMessage({ type: "REQ_MEETING_COST_NO_AUTH" }, "*");
		return () => {
			window.removeEventListener("message", handleWindowMessage);
		};
	}, []);
	return (
		<div className={classNames(styles.wrapper, { [styles.noCostData]: !meetingCost })}>
			<div className={styles.head}>
				<Button className={styles.closeBtn} variant="tertiary" onClick={onCloseMeetingPanel} size="slim">
					<FontAwesomeIcon icon={faTimes} fixedWidth />
				</Button>
				<img className={styles.headImg} src={bg} srcSet={`${bg} 1x, ${bg} 2x`} />
				<div className={styles.headTitle}>
					<div className={styles.headTitleText}>Welcome to</div>
					<div className={styles.headTitleLogo}>
						<FellowWordmark />
					</div>
				</div>
				{!meetingCost ? (
					<div className={styles.meetingCost}>
						<div className={styles.meetingCostCTA}>
							<Link to={signupURL} data-test-id="pre-auth-sign-up-no-meeting-cost-cta">
								Get started with a collaborative agenda
								<FontAwesomeIcon icon={faArrowDown} fixedWidth />
							</Link>
						</div>
					</div>
				) : (
					<div className={styles.meetingCost}>
						<div className={styles.meetingCostSummary}>
							<div className={styles.meetingCostLine1}>
								Meeting Cost:
								<span className={styles.meetingCostAmount}>
									{formatCostNumber(meetingDuration ? meetingCost * meetingDuration : meetingCost).toLocaleString()}
								</span>
								{meetingDuration ? "" : "/hour"}
								&nbsp;
								<Tooltip
									placement="auto"
									content={
										<div className={styles.meetingCostInfoTooltip}>
											Estimated cost based on attendees & time.
											<br />
											Highlighted to inspire effective time use. No real employee data used.
										</div>
									}
								>
									<FontAwesomeIcon icon={faInfoCircle} fixedWidth />
								</Tooltip>
							</div>
						</div>
						<div className={styles.meetingCostCTA}>
							<Link to={signupURL} target="_blank" data-test-id="pre-auth-sign-up-meeting-cost-cta">
								Make it count by adding your notes
								<FontAwesomeIcon icon={faArrowDown} fixedWidth />
							</Link>
						</div>
					</div>
				)}
			</div>
			<Link to={signupURL} target="_blank" data-test-id="pre-auth-sign-up-note">
				<div className={styles.fakeNote}>
					<ProseReact controlledDoc={FAKE_NOTE_CONTENT_NODE} editable config={FAKE_NOTE_EDITOR_CONFIG} />
				</div>
			</Link>
			<div className={styles.footer}>
				<div className={styles.footerGradient}></div>
				<div className={styles.footerContent}>
					<div className={styles.footerActions}>
						<Link to={loginURL} data-test-id="pre-auth-login-btn">
							<Button variant="primary" size="normal">
								Log in
							</Button>
						</Link>
						<Link to={signupURL} target="_blank" data-test-id="pre-auth-sign-up-btn">
							<Button variant="secondary" size="normal">
								Sign up
							</Button>
						</Link>
					</div>
					<div className={styles.footerNotice}>
						<a href="https://fellow.app" data-test-id="pre-auth-learn-more" target="_blank" rel="noopener">
							Learn more about Fellow.app
						</a>
					</div>
				</div>
			</div>
		</div>
	);
}
